import React, { useState, useEffect } from "react";
import { Scale, Info, Trash2, X } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { db } from "../firebase"; // Import the Firestore instance
import { collection, getDocs, query, where, deleteDoc, doc, writeBatch } from "firebase/firestore"; // Import Firestore functions
import { useAuth } from "../context/AuthContext";
import { getUserLabels } from "../services/labelService";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import CaseSearchFilter from "../components/CaseSearchFilter";
import PageHeader from "../components/PageHeader";
import { getStatusBucket } from "../utils/caseStatus";

function DisposedCases() {
  const { globalUser } = useAuth();
  const { status } = useParams();
  const [disposedCases, setDisposedCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingCasesCount, setPendingCasesCount] = useState(0);
  const [disposedCasesCount, setDisposedCasesCount] = useState(0); // State for disposed cases count
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isSelectionMode, setIsSelectionMode] = useState(false); // Bulk delete mode
  const [selectedCases, setSelectedCases] = useState([]); // Selected cases for deletion
  const [showConfirmation, setShowConfirmation] = useState(false); // Confirmation modal
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false); // Delete All confirmation

  // Helper function to get label by ID
  const getLabelById = (labelId) => {
    return labels.find((label) => label.id === labelId);
  };

  // Helper function to check if next hearing date is today, null, or in the past
  const shouldShowUpdateBanner = (nextHearingDate, status) => {
    // Don't show banner for disposed cases
    if (getStatusBucket(status) === "disposed") return false;

    // Show banner if date is null/empty
    if (!nextHearingDate) return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    // Parse the date string (format: DD/MM/YYYY)
    const [day, month, year] = nextHearingDate.split('/').map(Number);
    const hearingDate = new Date(year, month - 1, day); // month is 0-indexed
    hearingDate.setHours(0, 0, 0, 0);

    // Show banner if hearing date is today or in the past
    return hearingDate <= today;
  };

  // Fetch user labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!globalUser?.uid) return;
      try {
        const userLabels = await getUserLabels(globalUser.uid);
        setLabels(userLabels);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };
    fetchLabels();
  }, [globalUser]);

  // Fetch disposed cases from Firestore
  useEffect(() => {
    const fetchDisposedCases = async () => {
      if (!globalUser) {
        setIsLoading(false);
        return; // Early return if user is not authenticated
      }

      setIsLoading(true);

      let caseList = [];
      const pendingCollection = collection(db, "pending"); // Reference to the "pending" collection
      const disposedCollection = collection(db, "disposed"); // Reference to the "disposed" collection

      // Create a query to filter cases by the current user's ID
      const pendingCasesQuery = query(
        pendingCollection,
        where("owner", "==", globalUser.uid)
      );
      const disposedCasesQuery = query(
        disposedCollection,
        where("owner", "==", globalUser.uid)
      );

      // Fetch documents from the pending collection
      const pendingSnapshot = await getDocs(pendingCasesQuery);
      pendingSnapshot.forEach((doc) => {
        const caseData = doc.data(); // Get the data from the document
        const newCase = {
          id: doc.id, // Use the document ID
          caseNumber: caseData.caseNo,
          type: caseData.caseType,
          petitionerName: caseData.petitionerName,
          court: caseData.courtName,
          status: caseData.status || "Status not available", // Fetching status field
          nextHearingDate: caseData.nextHearingDate || null,
          labels: caseData.labels || [],
          labelsByUser: caseData.labelsByUser || {},
          collection: "pending", // Track which collection the case is from
        };
        caseList.push(newCase); // Add the new case to the list
      });

      // Fetch documents from the disposed collection
      const disposedSnapshot = await getDocs(disposedCasesQuery);
      disposedSnapshot.forEach((doc) => {
        const caseData = doc.data(); // Get the data from the document
        const newCase = {
          id: doc.id, // Use the document ID
          caseNumber: caseData.caseNo,
          type: caseData.caseType,
          petitionerName: caseData.petitionerName,
          court: caseData.courtName,
          status: caseData.status || "Status not available", // Fetching status field
          nextHearingDate: caseData.nextHearingDate || null,
          labels: caseData.labels || [],
          labelsByUser: caseData.labelsByUser || {},
          collection: "disposed", // Track which collection the case is from
        };
        caseList.push(newCase); // Add the new case to the list
      });

      if (status === "disposed") {
        const filteredDisposedCases = caseList.filter(
          (caseItem) => getStatusBucket(caseItem.status) === "disposed"
        );
        setDisposedCases(filteredDisposedCases);
      }

      else if (status === "rejected" || status === "dismissed") {
        const filteredDisposedCases = caseList.filter(
          (caseItem) => caseItem.status.toLowerCase() === status.toLocaleLowerCase()
        );
        setDisposedCases(filteredDisposedCases);
      }

      // Filter disposed cases


      // Count pending cases
      const pendingCount = caseList.filter(
        (caseItem) => getStatusBucket(caseItem.status) === "pending"
      ).length;
      setPendingCasesCount(pendingCount);

      // Count disposed cases
      // const disposedCount = filteredDisposedCases.length; // Use the filtered list for disposed count
      // setDisposedCasesCount(disposedCount);

      setIsLoading(false);
    };

    fetchDisposedCases();
  }, [globalUser]);

  // Helper function to delete events and cancel reminders for a case
  const cleanupEventsAndReminders = async (caseNumber, userId) => {
    if (!caseNumber || !userId) {
      console.log("Missing caseNumber or userId for cleanup");
      return;
    }

    console.log(`Cleaning up events and reminders for case: ${caseNumber}`);

    try {
      const batch = writeBatch(db);
      let eventsDeleted = 0;
      let remindersDeleted = 0;

      // 1. Delete events from lawyers/{uid}/events where "Case No" matches
      const eventsQuery = query(
        collection(db, "lawyers", userId, "events"),
        where("Case No", "==", caseNumber)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      eventsSnapshot.docs.forEach((eventDoc) => {
        batch.delete(eventDoc.ref);
        eventsDeleted++;
      });

      // 2. Delete reminders from eventReminders where caseNo matches
      const remindersQuery = query(
        collection(db, "eventReminders"),
        where("caseNo", "==", caseNumber),
        where("recipientId", "==", userId)
      );
      const remindersSnapshot = await getDocs(remindersQuery);

      remindersSnapshot.docs.forEach((reminderDoc) => {
        batch.delete(reminderDoc.ref);
        remindersDeleted++;
      });

      // Commit the batch
      if (eventsDeleted > 0 || remindersDeleted > 0) {
        await batch.commit();
        console.log(`Cleanup complete: ${eventsDeleted} events deleted, ${remindersDeleted} reminders deleted`);
      } else {
        console.log("No events or reminders found to clean up");
      }
    } catch (error) {
      console.error("Error cleaning up events and reminders:", error);
    }
  };

  // Handle deletion of selected cases
  const handleDelete = async () => {
    setShowConfirmation(false);
    const pendingToDelete = selectedCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = selectedCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Get case numbers for cleanup before deleting
      const casesToCleanup = selectedCases.map((c) => c.caseNumber).filter(Boolean);

      // Clean up events and reminders for each case
      if (globalUser?.uid && casesToCleanup.length > 0) {
        await Promise.all(
          casesToCleanup.map((caseNumber) =>
            cleanupEventsAndReminders(caseNumber, globalUser.uid)
          )
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }

      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      setDisposedCases((prevCases) => prevCases.filter((c) => !selectedCases.some((s) => s.id === c.id)));
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting cases: ", error);
    }
  };

  // Handle deletion of all displayed cases
  const handleDeleteAll = async () => {
    setShowDeleteAllConfirmation(false);
    const pendingToDelete = disposedCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = disposedCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Clean up events and reminders for each case
      if (globalUser?.uid && disposedCases.length > 0) {
        await Promise.all(
          disposedCases
            .map((c) => c.caseNumber)
            .filter(Boolean)
            .map((caseNumber) => cleanupEventsAndReminders(caseNumber, globalUser.uid))
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }
      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      setDisposedCases([]);
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting all cases: ", error);
    }
  };

  // Toggle case selection
  const toggleCaseSelection = (caseItem) => {
    const isSelected = selectedCases.some((c) => c.id === caseItem.id);
    if (isSelected) {
      setSelectedCases(selectedCases.filter((c) => c.id !== caseItem.id));
    } else {
      setSelectedCases([...selectedCases, caseItem]);
    }
  };

  // Reset to page 1 whenever the filtered result set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredCases]);

  return (
    <div className="min-h-screen bg-[#0c0a29] text-white relative">
      <Navbar />
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>

      <main className="px-4 sm:px-6 md:px-8 py-8">
        <PageHeader
          title="Disposed Cases"
          subtitle="Closed and disposed matters"
        />

        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center gap-6 mb-10 mt-1 w-11/12 md:w-1/2">
            {/* Skeleton for metric cards while loading */}
            {isLoading ? (
              <>
                <div className="bg-[#2d2a5a] rounded-lg p-6 text-center w-96 animate-pulse">
                  <div className="h-8 bg-[#3d3a6a] rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-10 bg-[#3d3a6a] rounded w-1/2 mx-auto"></div>
                </div>
                <div className="bg-[#2d2a5a] rounded-lg p-6 text-center w-96 animate-pulse">
                  <div className="h-8 bg-[#3d3a6a] rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-10 bg-[#3d3a6a] rounded w-1/2 mx-auto"></div>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/portal/pendingcases/pending"
                  className="bg-white bg-opacity-80 rounded-lg p-6 hover:bg-opacity-60 transition cursor-pointer text-center w-96"
                >
                  <h3 className="text-black text-2xl mb-2">PENDING CASES</h3>
                  <p className="text-black text-3xl font-bold">
                    {pendingCasesCount}
                  </p>
                </Link>
                <div
                  className="bg-yellow-300 bg-opacity-60 rounded-lg p-6 hover:bg-opacity-80 transition cursor-pointer text-center w-96"
                >
                  <h3 className="text-xl mb-2 text-black">{status.toUpperCase()} CASES</h3>
                  <p className="text-3xl font-bold text-black">
                    {disposedCases.length}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Bulk Delete Controls - Below metric cards */}
          {!isLoading && disposedCases.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 w-11/12 md:w-3/4 mb-4">
              <div className="flex items-center justify-end">
              {isSelectionMode ? (
                <div className="flex items-center space-x-3">
                  <span className="text-gray-400 text-sm">{selectedCases.length} selected</span>
                  <button
                    onClick={() => setShowConfirmation(true)}
                    className="p-2 rounded-full hover:bg-gray-700 text-red-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectedCases.length === 0}
                    title="Delete Selected"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteAllConfirmation(true)}
                    className="px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors duration-150"
                    title="Delete All Cases"
                  >
                    Delete All
                  </button>
                  <button
                    onClick={() => {
                      setIsSelectionMode(false);
                      setSelectedCases([]);
                    }}
                    className="p-2 rounded-full hover:bg-gray-700 text-yellow-500 transition-colors duration-150"
                    title="Cancel Selection"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsSelectionMode(true)}
                  className="p-2 rounded-full hover:bg-gray-700 text-red-500 transition-colors duration-150"
                  title="Enable Delete Mode"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
              </div>
              <CaseSearchFilter
                cases={disposedCases}
                onFilteredChange={setFilteredCases}
                className="w-full sm:w-2/5"
              />
            </div>
          )}
        </div>

        {/* Case Cards */}
        <div className="md:px-12">
          {/* Skeleton Loading State */}
          {isLoading && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#1e1b4b] rounded-lg p-4 animate-pulse h-[200px]">
                  <div className="flex h-full">
                    <div className="mr-4 flex-shrink-0">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#2d2a5a] rounded-md"></div>
                    </div>
                    <div className="flex-grow flex flex-col gap-2">
                      <div className="h-4 bg-[#2d2a5a] rounded w-3/4"></div>
                      <div className="h-6 bg-[#2d2a5a] rounded w-full"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-1/2"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-2/3"></div>
                      <div className="h-3 bg-[#2d2a5a] rounded w-1/2"></div>
                      <div className="mt-auto h-4 bg-[#2d2a5a] rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No cases message */}
          {!isLoading && disposedCases.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <Scale size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-xl">No {status} cases found.</p>
            </div>
          )}

          {/* No results from filter, but cases exist */}
          {!isLoading && disposedCases.length > 0 && filteredCases.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <Scale size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-xl">No cases match your current filters.</p>
              <p>Try adjusting your search or filter criteria.</p>
            </div>
          )}

          {/* Cases list */}
          {!isLoading && filteredCases.length > 0 && (
            <>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((caseItem) => {
                const isDisposed = getStatusBucket(caseItem.status) === "disposed";
                const showAlert = shouldShowUpdateBanner(caseItem.nextHearingDate, caseItem.status);
                // Get the current user's label IDs
                const userLabelIds = caseItem.labelsByUser?.[globalUser?.uid] || caseItem.labels || [];
                const hasLabels = userLabelIds && userLabelIds.length > 0;

                // Check if case is selected
                const isSelected = selectedCases.some((c) => c.id === caseItem.id);

                // Determine padding based on what's shown at the top
                const needsExtraPadding = (showAlert && !isDisposed) && hasLabels;
                let paddingClass = 'pt-8 pb-4 px-4';
                if (needsExtraPadding) {
                  paddingClass = 'pt-11 pb-4 px-4';
                }

                const handleCaseClick = (e) => {
                  if (isSelectionMode) {
                    e.preventDefault();
                    toggleCaseSelection(caseItem);
                    return;
                  }
                };

                return (
                  <div
                    key={caseItem.id}
                    className={`relative rounded-lg transition-all duration-200 h-full min-h-[200px] ${
                      isSelectionMode && isSelected ? 'ring-2 ring-yellow-500 shadow-lg' : ''
                    }`}
                  >
                    <Link
                      to={isSelectionMode ? '#' : `/portal/case/${caseItem.id}?from=disposed`}
                      onClick={handleCaseClick}
                      className={`block bg-[#1e1b4b] rounded-lg hover:bg-[#2d2a5a] transition-colors duration-200 h-full group ${paddingClass}`}
                    >
                      {/* Selection Checkbox */}
                      {isSelectionMode && (
                        <div className="absolute top-2 left-2 z-20">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'bg-yellow-500 border-yellow-500' : 'border-gray-400 bg-transparent'
                          }`}>
                            {isSelected && (
                              <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Alert Banner and Labels */}
                      <div className="absolute top-2 right-2 left-2 z-10">
                        <div className="flex items-start justify-end gap-2 flex-wrap">
                          {showAlert && !isDisposed && (
                            <div className="bg-[#C99F4A] text-white px-2 py-1 rounded-md flex items-center gap-1 shadow-md">
                              <Info size={12} />
                              <span className="text-xs font-semibold whitespace-nowrap">Update Next Hearing</span>
                            </div>
                          )}

                          {/* Label Badges */}
                          {hasLabels && (
                            <div className="flex flex-wrap gap-1 justify-end">
                              {userLabelIds.slice(0, 2).map((labelId) => {
                                const label = getLabelById(labelId);
                                if (!label) return null;
                                return (
                                  <div
                                    key={labelId}
                                    className="px-2 py-0.5 rounded-full text-white text-xs font-medium shadow-sm"
                                    style={{ backgroundColor: label.color }}
                                  >
                                    {label.name}
                                  </div>
                                );
                              })}
                              {userLabelIds.length > 2 && (
                                <div className="px-2 py-0.5 rounded-full bg-gray-600 text-white text-xs font-medium shadow-sm">
                                  +{userLabelIds.length - 2}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex h-full">
                        <div className="mr-4 flex-shrink-0 flex items-start">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#2d2a5a] rounded-md">
                            <Scale size={32} color="#FFD700" strokeWidth={1.5} />
                          </div>
                        </div>
                        <div className="flex-grow overflow-hidden flex flex-col">
                          <p className="text-sm text-gray-400 truncate">
                            Case Number: {caseItem.caseNumber}
                          </p>
                          <h3 className="text-xl font-bold text-white truncate">{caseItem.type}</h3>
                          <p className="text-sm text-gray-300 truncate">Party Name: {caseItem.petitionerName}</p>
                          <p className="text-sm text-gray-300 truncate">Court: {caseItem.court}</p>
                          {!isDisposed && (
                            <p className="text-sm text-gray-300 truncate">
                              Next Hearing Date: {caseItem.nextHearingDate || "N/A"}
                            </p>
                          )}
                          <p className={`text-sm font-semibold truncate ${
                            getStatusBucket(caseItem.status) === "disposed" ? "text-red-400" : "text-green-400"
                          }`}>
                            Status: {caseItem.status}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={filteredCases.length}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
            </>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h3 className="text-xl font-semibold mb-4 text-purple-600">Confirm Deletion</h3>
            <p className="text-sm mb-6">Are you sure you want to delete {selectedCases.length} selected case(s)? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h3 className="text-xl font-semibold mb-2 text-red-600">Delete All Cases</h3>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
            <p className="text-sm mb-6">
              Are you sure you want to delete <span className="font-semibold text-red-600">all {disposedCases.length} case(s)</span>? This will permanently remove all disposed cases shown on this page.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteAllConfirmation(false)}
                className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DisposedCases;
